import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useNRMutation from '../../../../graphql/UseNRMutation';
import useNRQuery from '../../../../graphql/useNRQuery';
import { GET_VULNERABILITY_JIRA_TICKETS, QL_JIRA_INTEGRATION_UNLINK_VULNERABILITY_TICKET } from '../../../../graphql/queries/jiraIntegration';
import JiraIcon from '../../../../NRIcons/IntegrationIcons/JiraIcon';
import useIsJiraIntegrationActive from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/useIsJiraIntegrationActive';
import NRJiraTicketsTable from './NRJiraTicketsTable';
import NRJiraUnlinkTicketModal from './NRJiraUnlinkTicketModal';
import { getTicketCountLabel, VULNERABILITY_JIRA_TICKETS_LABELS } from './vulnerabilityJiraTickets.constants';

function NRVulnerabilityJiraTickets({ advisoryId, identificationIds = [], sx }) {
  const theme = useTheme();
  const { componentId: assetId } = useParams();
  const { isJiraIntegrationActive } = useIsJiraIntegrationActive();
  const [ticketPendingUnlink, setTicketPendingUnlink] = useState(null);

  const ticketArgs = useMemo(
    () => ({
      assetId,
      advisoryId,
      identificationIds
    }),
    [advisoryId, assetId, identificationIds]
  );

  const ticketsRefetch = useMemo(
    () => ({
      refetchQueries: [{ query: GET_VULNERABILITY_JIRA_TICKETS, variables: { args: ticketArgs } }]
    }),
    [ticketArgs]
  );

  const [tickets, ticketsLoading] = useNRQuery({
    query: GET_VULNERABILITY_JIRA_TICKETS,
    options: {
      variables: { args: ticketArgs },
      skip: !isJiraIntegrationActive || !assetId || !advisoryId
    },
    qlObjKey: 'vulnerabilityJiraTickets',
    defaultValue: []
  });

  const [unlinkTicket, unlinkResult, isUnlinking] = useNRMutation(
    QL_JIRA_INTEGRATION_UNLINK_VULNERABILITY_TICKET,
    'jiraIntegrationUnlinkVulnerabilityTicket',
    VULNERABILITY_JIRA_TICKETS_LABELS.UNLINK_SUCCESS,
    VULNERABILITY_JIRA_TICKETS_LABELS.UNLINK_ERROR,
    ticketsRefetch
  );

  const pendingUnlinkTicketIdRef = useRef(null);

  useEffect(() => {
    if (!pendingUnlinkTicketIdRef.current || !unlinkResult || !ticketPendingUnlink) {
      return;
    }

    if (ticketPendingUnlink.id !== pendingUnlinkTicketIdRef.current) {
      return;
    }

    if (unlinkResult.success) {
      pendingUnlinkTicketIdRef.current = null;
      setTicketPendingUnlink(null);
      return;
    }

    if (unlinkResult.success === false) {
      pendingUnlinkTicketIdRef.current = null;
    }
  }, [unlinkResult, ticketPendingUnlink]);

  const handleCreateTicket = useCallback(event => {
    event?.stopPropagation();
    // Create ticket flow will be implemented separately.
  }, []);

  const handleUnlinkRequest = useCallback(ticket => {
    setTicketPendingUnlink(ticket);
  }, []);

  const handleUnlinkClose = useCallback(() => {
    if (!isUnlinking) {
      pendingUnlinkTicketIdRef.current = null;
      setTicketPendingUnlink(null);
    }
  }, [isUnlinking]);

  const handleUnlinkConfirm = useCallback(() => {
    if (!ticketPendingUnlink) {
      return;
    }

    pendingUnlinkTicketIdRef.current = ticketPendingUnlink.id;

    unlinkTicket({
      variables: {
        args: {
          ...ticketArgs,
          ticketId: ticketPendingUnlink.id
        }
      }
    });
  }, [ticketArgs, ticketPendingUnlink, unlinkTicket]);

  if (!isJiraIntegrationActive) {
    return null;
  }

  const sectionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    mt: theme.spacing(2)
  };

  const ticketCountLabel = tickets.length ? getTicketCountLabel(tickets.length) : VULNERABILITY_JIRA_TICKETS_LABELS.NO_TICKETS;

  const sectionHeaderContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Avatar
          sx={{
            width: theme.spacing(6),
            height: theme.spacing(6),
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.iconInfo
          }}
        >
          <JiraIcon width={theme.spacing(4)} height={theme.spacing(4)} />
        </Avatar>
        <Box sx={{ ml: theme.spacing(1), minWidth: 0 }}>
          <Typography variant="label" color="text.secondary">
            {VULNERABILITY_JIRA_TICKETS_LABELS.SECTION_LABEL}
          </Typography>
          <Typography variant="h3" color="text.primary">
            {ticketCountLabel}
          </Typography>
        </Box>
      </Box>
      <Button variant="outlined" color="secondary" onClick={handleCreateTicket} sx={{ flexShrink: 0, ml: theme.spacing(2) }}>
        {VULNERABILITY_JIRA_TICKETS_LABELS.CREATE_TICKET}
      </Button>
    </Box>
  );

  const sectionHeader = <Box sx={{ p: theme.spacing(2) }}>{sectionHeaderContent}</Box>;

  return (
    <>
      <Box sx={[sectionSx, sx]}>
        {ticketsLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: theme.spacing(3)
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : tickets.length ? (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: theme.spacing(2),
                py: theme.spacing(2),
                '& .MuiAccordionSummary-expandIconWrapper': {
                  order: -1,
                  marginRight: theme.spacing(1.5),
                  marginLeft: 0
                },
                '& .MuiAccordionSummary-content': {
                  order: 0,
                  margin: 0,
                  width: '100%'
                }
              }}
            >
              {sectionHeaderContent}
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <NRJiraTicketsTable tickets={tickets} onUnlink={handleUnlinkRequest} />
            </AccordionDetails>
          </Accordion>
        ) : (
          sectionHeader
        )}
      </Box>

      <NRJiraUnlinkTicketModal
        open={Boolean(ticketPendingUnlink)}
        ticket={ticketPendingUnlink}
        onClose={handleUnlinkClose}
        onConfirm={handleUnlinkConfirm}
        isSubmitting={isUnlinking}
      />
    </>
  );
}

export default NRVulnerabilityJiraTickets;
